import React, { Ref, useState } from "react";
import { useTranslation } from "react-i18next";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import { IconButton, Popover } from "@mui/material";
import { BaseTextFieldProps } from "@mui/material/TextField";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import {
  StaticDatePicker as MuiStaticDatePicker,
  StaticDatePickerProps as MuiStaticDatePickerProps,
} from "@mui/x-date-pickers/StaticDatePicker";

export type TStaticDatePickerProps = Omit<
  MuiStaticDatePickerProps<Dayjs>,
  "label" | "value"
> &
  Pick<BaseTextFieldProps, "id" | "sx"> & {
    value?: Dayjs | null;
    buttonLabel?: string;
    onAccept?: (value: Dayjs | null) => void;
  };

const StaticDatePicker = React.forwardRef(
  (
    {
      id,
      value,
      disabled,
      buttonLabel,
      onAccept,
      ...props
    }: TStaticDatePickerProps,
    ref: Ref<HTMLDivElement>,
  ) => {
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

    const { i18n } = useTranslation();

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
      setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
      setAnchorEl(null);
    };

    const handleAccept = (value: Dayjs | null) => {
      onAccept?.(value);
      handleClose();
    };

    return (
      <>
        <IconButton
          role="button"
          aria-label={buttonLabel}
          aria-haspopup="dialog"
          onClick={handleClick}
          disabled={disabled}
        >
          <CalendarTodayIcon
            sx={{ fontSize: "1.5rem" }}
            data-qaid="calendar-today-icon"
          />
        </IconButton>
        <Popover
          id={id}
          open={!!anchorEl}
          anchorEl={anchorEl}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
          onClose={handleClose}
        >
          <LocalizationProvider
            dateAdapter={AdapterDayjs}
            adapterLocale={i18n.language}
          >
            <MuiStaticDatePicker
              data-testid={id}
              disabled={disabled}
              displayStaticWrapperAs="desktop"
              value={value}
              ref={ref}
              onAccept={handleAccept}
              slotProps={{
                day: {
                  onClick: () => {
                    handleClose();
                  },
                },
              }}
              {...props}
            />
          </LocalizationProvider>
        </Popover>
      </>
    );
  },
);

export default StaticDatePicker;
